import type { GlobalStore } from '../../../state/zustand';
import type { ScenarioLocation } from '../../../lib/planningData';
import type { PortCallData } from '../../core/Core';
import {
  getIntegrationPortCosts,
  type PortCostByVcMaps,
} from './portCostIntegration';
import {
  getDefaultPortCostByType,
  type PortCostEstimateMaps,
} from '../../hooks/usePortCostEstimates';
import type { usePortCostsCoa } from '../../hooks/usePortCostCoa';
import type { DefaultMap } from '@seaber/common/lib/collections';
import type { ClientId } from '../../core/ClientId';
import type { Currency, ScenarioTimeline_ShipFragment } from '../../../graphql';
import type { VoyageGroup } from '../../core/Timelines';
import {
  isPortCallEventTimeData,
  isTCOrderEventTimeData,
  type Events,
} from '../../core/TimeData';
import { getPortCostDefaults } from './portCostDefault';
import {
  getHistoricalPortCostEstimates,
  mapExternalPortCostEstimatesToParams,
} from './portCostHistory';
import type { CostsByPortCall } from './costs';
import { type PortCostPredictionByPortCall } from './portCostPrediction';
import { type MarcuraPortCosts } from '../../marcura/context';

type ShipmentsByPortCallId = DefaultMap<ClientId | null, Set<ClientId | null>>;

export function getPortCosts({
  currencyRates,
  baseCurrency,
  locationById,
  portCalls,
  portCostByVcMaps,
  portCostEstimateMaps,
  portCostByCoaMaps,
  portCostMarcuraMaps,
  portCostPredictions,
  shipmentsByPortCallId,
  vessel,
  voyageGroups,
}: {
  currencyRates: GlobalStore['currencyRates'];
  baseCurrency: Currency;
  locationById: Map<number, ScenarioLocation>;
  portCalls: PortCallData[];
  portCostByVcMaps: PortCostByVcMaps;
  portCostEstimateMaps: PortCostEstimateMaps;
  portCostByCoaMaps: ReturnType<typeof usePortCostsCoa>;
  portCostMarcuraMaps: MarcuraPortCosts;
  portCostPredictions: PortCostPredictionByPortCall | null;
  shipmentsByPortCallId: ShipmentsByPortCallId;
  vessel: ScenarioTimeline_ShipFragment;
  voyageGroups: VoyageGroup<Events>[];
}): CostsByPortCall {
  const costs: CostsByPortCall = new Map();
  const vesselName = vessel.name;
  const vesselId = vessel.id;
  const vesselWeight = vessel.weightCapacity;
  const primaryVesselAttributeId = vessel.primaryAttributeId;
  if (!vesselName) return costs;

  const ports = new Set<string>();
  for (const pc of portCalls) {
    const name = locationById.get(pc.locationId)?.name;
    if (name) {
      ports.add(name);
    }
  }

  const {
    byName,
    byPrimaryAttributeId,
    defaultById,
    defaultByTypeId,
    defaultByVesselId,
    locationIdsByName,
  } = portCostEstimateMaps;

  const portCostDefaults = getPortCostDefaults({
    portCostEstimateMaps,
    vessel,
    portCalls,
    locationById,
    locationIdsByName,
  });

  const integrationPortCosts = getIntegrationPortCosts(
    voyageGroups,
    portCostByVcMaps,
    locationById,
    portCostMarcuraMaps,
    vesselWeight,
    baseCurrency,
  );

  for (const pc of portCalls) {
    const firstPortCallEvent = pc?.tds.find(isPortCallEventTimeData);
    const portCallEntry = firstPortCallEvent?.event.entry;
    const tcTimeData = pc.tds.filter(isTCOrderEventTimeData);

    const orders = tcTimeData.map((entry) => entry.event.order);
    const locationId = pc.locationId;
    const location = locationById.get(locationId);
    const locationName = location?.name;
    if (!firstPortCallEvent || !locationName || !portCallEntry) continue;

    // Port costs are ignored for canal transit port calls
    if (firstPortCallEvent.event.isCanalTransitPortCall) {
      continue;
    }

    const {
      oneTimeOverrideCost,
      useHistoricalPortCost,
      useOneTimeOverrideCost,
      useIntegrationPortCost,
      portCostDefaultId,
    } = portCallEntry;

    let selectedId: number | undefined;
    let portCostEstimate: number | undefined;
    let coaPortCost: { value: number; currency: string } | undefined;

    const numShipments = shipmentsByPortCallId.get(pc.id).size;
    if (portCostByCoaMaps) {
      for (const order of orders) {
        if (!order.coaId) continue;
        const portCostsByVesselId = portCostByCoaMaps
          .get(order.coaId)
          .get(vesselId)
          .get(locationId);
        const portCostsByVesselClassId = portCostByCoaMaps
          .get(order.coaId)
          .get(primaryVesselAttributeId ? -primaryVesselAttributeId : null)
          .get(locationId);
        const portCost = portCostsByVesselId[0] || portCostsByVesselClassId[0];

        if (portCost) {
          coaPortCost = portCost
            ? {
                value: +portCost.cost,
                currency: portCost.coaContractByCoaContractId?.currency ?? '',
              }
            : undefined;
          break;
        }
      }
    }

    if (oneTimeOverrideCost && useOneTimeOverrideCost) {
      portCostEstimate = oneTimeOverrideCost;
    }

    if (!portCostEstimate && portCostDefaultId) {
      portCostEstimate = defaultById.get(portCostDefaultId);
      selectedId = portCostDefaultId;
    }

    const integrationCost = integrationPortCosts.get(pc.id);
    if (useIntegrationPortCost && integrationCost) {
      portCostEstimate = integrationCost.value;
    }

    if (
      !portCostEstimate &&
      primaryVesselAttributeId &&
      !useHistoricalPortCost
    ) {
      const defByTypeIdCost = getDefaultPortCostByType(
        defaultByTypeId,
        locationId,
        primaryVesselAttributeId,
        locationIdsByName,
      );

      if (defByTypeIdCost) {
        selectedId = defByTypeIdCost.id;
        portCostEstimate = defByTypeIdCost.cost;
      }
    }

    const defByNameCost = defaultByVesselId.get(locationId, vesselId);
    if (!portCostEstimate && defByNameCost) {
      selectedId = defByNameCost.id;
      portCostEstimate = defByNameCost.cost;
    }

    if (!portCostEstimate) {
      const typeIds = vessel?.vesselAttributesAssociationsByShipId.nodes ?? [];
      for (const type of typeIds) {
        const defByTypeIdCost =
          type?.vesselAttributeId &&
          defaultByTypeId.get(locationId, type.vesselAttributeId);
        if (defByTypeIdCost) {
          selectedId = type.id;
          portCostEstimate = defByTypeIdCost.cost;
          break;
        }
      }
    }

    const historicalValue = getHistoricalPortCostEstimates({
      locationName,
      vesselName,
      byName,
      primaryVesselAttributeId,
      byPrimaryAttributeId,
    });

    if ((!portCostEstimate || useHistoricalPortCost) && locationName) {
      portCostEstimate = historicalValue?.cost;
      selectedId = undefined;
    }

    const portCostPrediction = portCostPredictions?.get(pc.id);
    const predictionCost = portCostPrediction
      ? {
          value: portCostPrediction.value,
          params: portCostPrediction.params,
        }
      : undefined;

    const defaultValue = portCostDefaults.get(locationName);

    const params = mapExternalPortCostEstimatesToParams(
      historicalValue,
      currencyRates,
    );
    costs.set(pc.id, [
      {
        type: 'PORT_CALL',
        name: 'Port cost',
        value: (coaPortCost?.value ?? portCostEstimate ?? 0) / numShipments,
        numShipments,
        params,
        locations: location ? [location] : [],
        options: {
          defaultValue,
          historicalValue,
          integrationCost,
          predictionCost,
          oneTimeOverrideCost,
          portCallEntry,
          selectedId,
          useHistoricalPortCost,
          useOneTimeOverrideCost,
          useIntegrationPortCost,
          coaPortCost,
        },
      },
    ]);
  }

  return costs;
}
